import { useEffect, useState, useMemo, useCallback } from 'react';
import { Command } from 'cmdk';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useLogout } from '@/hooks/useAuth';

/**
 * ⌘K / Ctrl-K palette. Single keyboard shortcut to jump anywhere in the
 * app or run a built-in action. Implemented over `cmdk` (the Radix-style
 * primitive used by Linear / Vercel / Raycast).
 *
 * Items are derived from the current route — when the user is inside a
 * workspace, palette items resolve `:wsId` so "Ask" routes to the right
 * /app/workspace/<wsId>/ask. When no workspace is active, palette only
 * shows global routes (workspace picker, settings, admin if applicable).
 */
export function CommandPalette() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const logout = useLogout();
  const { wsId } = useParams<{ wsId?: string }>();

  // Open with ⌘K / Ctrl-K from anywhere; close with Esc handled by Radix.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const go = useCallback((path: string) => { setOpen(false); navigate(path); }, [navigate]);

  const items = useMemo(() => {
    const ws = (path: string) => (wsId ? `/app/workspace/${wsId}/${path}` : '/app');
    const list: { id: string; label: string; hint?: string; section: string; run: () => void; keywords?: string[] }[] = [
      { id: 'ask', section: t('nav.group.work') as string, label: t('nav.ask') as string, run: () => go(ws('ask')), keywords: ['question', '質問'] },
      { id: 'docs', section: t('nav.group.work') as string, label: t('nav.docs') as string, run: () => go(ws('docs')), keywords: ['documents', '文書'] },
      { id: 'compose', section: t('nav.group.work') as string, label: t('compose.title') as string, run: () => go(ws('compose')) },
      { id: 'brief', section: t('nav.group.work') as string, label: t('brief_agent.title') as string, run: () => go(ws('brief')) },
      { id: 'compare', section: t('nav.group.work') as string, label: t('compare_agent.title') as string, run: () => go(ws('compare-docs')) },
      { id: 'audit', section: t('nav.group.work') as string, label: t('audit_agent.title') as string, run: () => go(ws('audit')) },
      { id: 'signals', section: t('nav.group.work') as string, label: t('signals.title') as string, run: () => go(ws('signals')) },
      { id: 'workflows', section: t('nav.group.work') as string, label: t('workflow.title') as string, run: () => go(ws('workflows')) },
      { id: 'approvals', section: t('nav.group.work') as string, label: t('nav.approvals') as string, run: () => go(ws('approvals')) },

      { id: 'connectors', section: t('nav.group.admin') as string, label: t('nav.connectors') as string, run: () => go(ws('connectors')) },
      { id: 'audit-log', section: t('nav.group.admin') as string, label: t('nav.audit') as string, run: () => go(ws('audit-log')) },
      { id: 'sso', section: t('nav.group.admin') as string, label: t('nav.sso') as string, run: () => go(ws('sso')) },
      { id: 'scim', section: t('nav.group.admin') as string, label: t('nav.scim') as string, run: () => go(ws('scim')) },
      { id: 'settings', section: t('nav.group.admin') as string, label: t('nav.settings') as string, run: () => go(ws('settings')) },

      { id: 'workspaces', section: t('nav.group.platform') as string, label: t('nav.workspaces', { defaultValue: 'Workspaces' }) as string, run: () => go('/app') },
      { id: 'admin', section: t('nav.group.platform') as string, label: t('nav.site_admin') as string, run: () => go('/admin'), keywords: ['admin'] },
      { id: 'changelog', section: t('nav.group.platform') as string, label: t('nav.whats_new') as string, run: () => go('/changelog') },
      { id: 'security', section: t('nav.group.platform') as string, label: t('nav.security_center') as string, run: () => go('/security') },

      { id: 'logout', section: t('nav.group.platform') as string, label: t('nav.logout') as string, hint: t('command.danger', { defaultValue: 'danger' }) as string, run: () => { setOpen(false); void logout(); } },
    ];
    return list;
  }, [wsId, go, logout, t]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="fixed inset-0 z-[60] bg-ink-900/30 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.14, ease: [0.16, 0.84, 0.44, 1] }}
            className="fixed left-1/2 top-[16vh] z-[61] -translate-x-1/2 w-[calc(100%-2rem)] max-w-xl"
          >
            <Command
              label={t('command.label', { defaultValue: 'Command palette' }) as string}
              className="card-paper overflow-hidden"
            >
              <Command.Input
                placeholder={t('command.placeholder', { defaultValue: 'Type a command or search…' }) as string}
                className="w-full px-5 py-4 text-[15px] text-ink-900 bg-transparent border-b border-ink-100 outline-none placeholder:text-ink-400"
              />
              <Command.List className="max-h-[55vh] overflow-y-auto py-2">
                <Command.Empty className="px-5 py-6 text-[13px] text-ink-500 text-center">
                  {t('command.empty', { defaultValue: 'No matches.' })}
                </Command.Empty>
                {groupBy(items, (i) => i.section).map(([section, group]) => (
                  <Command.Group
                    key={section}
                    heading={section}
                    className="px-2 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.14em] [&_[cmdk-group-heading]]:text-ink-400 [&_[cmdk-group-heading]]:font-semibold"
                  >
                    {group.map((it) => (
                      <Command.Item
                        key={it.id}
                        value={`${it.label} ${(it.keywords ?? []).join(' ')}`}
                        onSelect={it.run}
                        className={`flex items-center justify-between px-3 py-2 rounded-md text-[14px] cursor-pointer text-ink-700 aria-selected:bg-accent-50 aria-selected:text-ink-900 hover:bg-ink-50/60 ${
                          it.hint === 'danger' ? 'aria-selected:bg-warn-500/10 aria-selected:text-warn-600' : ''
                        }`}
                      >
                        <span className="truncate">{it.label}</span>
                        {it.hint && (
                          <span className="text-[10.5px] uppercase tracking-[0.12em] text-ink-400 font-semibold">
                            {it.hint}
                          </span>
                        )}
                      </Command.Item>
                    ))}
                  </Command.Group>
                ))}
              </Command.List>
              <div className="px-4 py-2 border-t border-ink-100 text-[11px] text-ink-500 flex items-center justify-between">
                <span>↑↓ {t('command.navigate', { defaultValue: 'navigate' })}</span>
                <span>↵ {t('command.select', { defaultValue: 'select' })}</span>
                <span>esc {t('command.close', { defaultValue: 'close' })}</span>
              </div>
            </Command>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function groupBy<T, K extends string>(items: T[], key: (item: T) => K): [K, T[]][] {
  const map = new Map<K, T[]>();
  for (const it of items) {
    const k = key(it);
    const arr = map.get(k);
    if (arr) arr.push(it); else map.set(k, [it]);
  }
  return Array.from(map.entries());
}
